import React, { ReactElement, useRef, useState } from "react";
import { useSnackbar } from "notistack";

import { RemoveImageLogo } from "pages/location/components/add-location/styles";
import {
  FieldContainer,
  InputLabelText,
  UploadInput,
  PdfPreviewWrapper,
  PdfPreviewLabel,
  PdfPreviewFrame,
  EmptyPreviewText,
  UploadSectionsRow,
  ClickablePdfPreview,
  PdfPreviewContainer,
} from "pages/management/warranty/styles";
import PdfPreviewModal from "pages/management/warranty/components/pdf-preview-modal";

interface PdfEditSectionProps {
  label: string;
  pdfUrl?: string;
  onUpload: (dataUrl: string) => void;
  onRemove: () => void;
}

function PdfEditSection({
  label,
  pdfUrl,
  onUpload,
  onRemove,
}: PdfEditSectionProps): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandOpen, setExpandOpen] = useState(false);
  const hasPdf = Boolean(pdfUrl?.trim());

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.type !== "application/pdf") {
      enqueueSnackbar("Please upload a PDF file", { variant: "error" });
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      onUpload(reader.result as string);
      enqueueSnackbar("Warranty PDF uploaded successfully", {
        variant: "success",
      });
    };
    reader.onerror = () => {
      enqueueSnackbar("Failed to read PDF file", { variant: "error" });
    };
    e.target.value = "";
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <FieldContainer>
      <InputLabelText>{label}</InputLabelText>
      <UploadInput
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFileChange}
      />
      {hasPdf ? (
        <>
          <PdfPreviewWrapper>
            <PdfPreviewLabel>PDF preview</PdfPreviewLabel>
            <PdfPreviewContainer>
              <ClickablePdfPreview
                role="button"
                tabIndex={0}
                onClick={() => setExpandOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setExpandOpen(true);
                  }
                }}
              >
                <PdfPreviewFrame src={pdfUrl} title={`${label} preview`} />
              </ClickablePdfPreview>
              <RemoveImageLogo onClick={handleRemove} />
            </PdfPreviewContainer>
          </PdfPreviewWrapper>
          <PdfPreviewModal
            open={expandOpen}
            onClose={() => setExpandOpen(false)}
            pdfUrl={pdfUrl}
            title={`${label} expanded view`}
          />
        </>
      ) : (
        <EmptyPreviewText>Upload a PDF to preview it here.</EmptyPreviewText>
      )}
    </FieldContainer>
  );
}

interface Props {
  standardPdfUrl?: string;
  extendedPdfUrl?: string;
  onStandardUpload: (dataUrl: string) => void;
  onExtendedUpload: (dataUrl: string) => void;
  onStandardRemove: () => void;
  onExtendedRemove: () => void;
}

function EditWarrantyPdfPanel({
  standardPdfUrl,
  extendedPdfUrl,
  onStandardUpload,
  onExtendedUpload,
  onStandardRemove,
  onExtendedRemove,
}: Props): ReactElement {
  return (
    <UploadSectionsRow>
      <PdfEditSection
        label="Upload standard Warranty PDF"
        pdfUrl={standardPdfUrl}
        onUpload={onStandardUpload}
        onRemove={onStandardRemove}
      />
      <PdfEditSection
        label="Upload extended Warranty PDF"
        pdfUrl={extendedPdfUrl}
        onUpload={onExtendedUpload}
        onRemove={onExtendedRemove}
      />
    </UploadSectionsRow>
  );
}

export default EditWarrantyPdfPanel;
